import React, { useState, useEffect } from 'react';
import type { Notebook, NotebookFormData } from '../../type/notebook';
import { notebookService } from './notebookService';
import NotebookItem from './notebookItem';
import type { UserModel } from '../../type/user';
import { getUser, isAuthenticated } from '../../auth/auth';
import { useLanguage } from '../../context/languageContext';
import NotebookModal from '../../modal/notebookModal/notebookModal';
import PageHeader from "../../components/pageHeader/pageHeader";
import EmptyState from "../../components/emptyState/emptyState";
import { useSpinner } from "../../components/spinner/spinner";
import './notebookPage.scss';

const NotebookPage: React.FC = () => {
  const [notebooks, setNotebooks] = useState<Notebook[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedNotebook, setSelectedNotebook] = useState<Notebook | null>(null);
  const [user, setUser] = useState<UserModel | null>(null);
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState<'private' | 'public'>('private');
  const {showSpinner, hideSpinner} = useSpinner();
  const [loading, setLoading] = useState(false);
  const { selectedLanguageId } = useLanguage();

  useEffect(() => {
    if (isAuthenticated()) {
      const userData = getUser();
      if (userData?.iduser) {
        setUser(userData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.iduser && selectedLanguageId) {
      loadNotebooks(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedLanguageId]);

  const loadNotebooks = async (currentUser?: UserModel) => {
    const activeUser = currentUser || user;  // ← fall back to state
    if (!activeUser?.iduser || !selectedLanguageId) return;

    setLoading(true);
    showSpinner();
    try {
      // Transient 403/network errors can happen while the backend/DB connection pool
      // is still warming up right after the app starts. Retry once before failing.
      let data: Notebook[];
      try {
        data = await notebookService.getNotebooks(activeUser.iduser, selectedLanguageId);
      } catch {
        await new Promise((r) => setTimeout(r, 800));
        data = await notebookService.getNotebooks(activeUser.iduser, selectedLanguageId);
      }
      setNotebooks(data);
    } catch (error) {
      console.error('Error loading notebooks:', error);
      //alert('Failed to load notebooks. Please try again.');
    } finally {
      setLoading(false);
      hideSpinner();
    }
  };

  const handleAddNotebook = () => {
    setSelectedNotebook(null);
    setShowModal(true);
  };

  const handleEditNotebook = (notebook: Notebook) => {
    setSelectedNotebook(notebook);
    setShowModal(true);
  };

  const handleSaveNotebook = async (data: NotebookFormData) => {
    if (!user?.iduser || !selectedLanguageId) return;

    if (selectedNotebook) {
      await notebookService.updateNotebook(selectedNotebook.idnotebook, data, () => loadNotebooks());
    } else {
      await notebookService.addNotebook(user.iduser, data, selectedLanguageId, () => loadNotebooks());
    }
  };

  const handleDeleteNotebook = async (notebook: Notebook) => {
    if (!user?.iduser) return;

    try {
      await notebookService.deleteNotebook(notebook.idnotebook, user.iduser, () => loadNotebooks());
    } catch (error) {
      console.error('Error deleting notebook:', error);
      alert('Failed to delete notebook. Please try again.');
    }
  };

  // Filter notebooks by tab and search
  const filteredNotebooks = notebooks
    .filter(notebook => {
      if (activeTab === 'private') {
        return !notebook.isPublic && notebook.iduser === user?.iduser;
      } else {
        return notebook.isPublic;
      }
    })
    .filter(notebook =>
      notebook.name.toLowerCase().includes(searchText.toLowerCase()) ||
      notebook.note.toLowerCase().includes(searchText.toLowerCase())
    );

  if (!user) {
    return (
      <div className="container mt-5"></div>
    );
  }

  return (
    <div className="notebook-page">
      <div className="container">
        <PageHeader
          title="My Notebooks"
          icon="bi-journal-text"
          addButton={{
            label: "New Notebook",
            onClick: handleAddNotebook,
          }}
          search={{
            value: searchText,
            onChange: setSearchText,
            placeholder: "Search notebooks...",
          }}
        />

        {/* Tabs for public/private */}
        <div className="notebook-page__tabs">
          <button
            className={`btn btn-outline-primary me-2 ${activeTab === 'private' ? 'active' : ''}`}
            onClick={() => setActiveTab('private')}
          >
            Private
          </button>
          <button
            className={`btn btn-outline-primary me-2  ${activeTab === 'public' ? 'active' : ''}`}
            onClick={() => setActiveTab('public')}
          >
            Public
          </button>
        </div>

        {loading ? (
          <div className="notebook-page__loading">
          </div>
        ) : filteredNotebooks.length === 0 ? (
          searchText ? (
            <EmptyState
              icon="bi bi-search"
              title="No notebooks found"
              description={`No notebooks match "${searchText}"`}
              button={{
                label: 'Clear search',
                onClick: () => setSearchText(''),
                variant: 'outline-primary'
              }}
            />
          ) : (
            <EmptyState
              icon="bi bi-journal-x"
              title="No notebooks yet"
              description="Create your first notebook to get started!"
              button={{
                label: 'New Notebook',
                onClick: handleAddNotebook,
                icon: 'bi bi-plus-lg'
              }}
            />
          )
        ) : (
          <div className="notebook-page__list">
            {filteredNotebooks.map((notebook) => (
              <NotebookItem
                key={notebook.idnotebook}
                notebook={notebook}
                user={user}
                onEdit={handleEditNotebook}
                onDelete={handleDeleteNotebook}
              />
            ))}
          </div>
        )}
      </div>

      <NotebookModal
        show={showModal}
        notebook={selectedNotebook}
        onHide={() => setShowModal(false)}
        onSave={handleSaveNotebook}
      />
    </div>
  );
};

export default NotebookPage;